"use client";
import { useAtom } from "jotai";
import { ReactFlowProvider } from "@xyflow/react";
import {
  BuiltInApp,
  RawCanvas,
  Session,
  Toggles,
  isKeyboardShortcutsModalOpenAtom,
  Account,
} from "@zapier/canvas-state";
import {
  CurrentAccountIdProvider,
  useCurrentAccountId,
} from "@zapier/identity";
import { PAGE_VIEW_EVENT } from "@zapier/canvas-constants";
import { useEffect } from "react";

import { Canvas } from "../components/Canvas";
import { CanvasHydrator } from "../components/CanvasHydrator";
import { BuilderNav } from "../components/BuilderNav";
import { KeyboardShortcutsModal } from "../components/KeyboardShortcutsModal";
import { Notifications } from "../components/Notifications";
import { CanvasBackground } from "../components/CanvasBackground";
import { CanvasCopilotHydrator } from "../components/CanvasCopilotHydrator";

import styles from "./IframeEmbeddedCanvas.module.css";

export function ViewOnlyCanvas({
  canvas,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  agentsApp,
  session,
  account,
  toggles,
}: {
  canvas: RawCanvas;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  agentsApp: BuiltInApp;
  session: Session;
  account: Account;
  toggles: Toggles;
}) {
  useEmitPageViewEvent();

  return (
    <CurrentAccountIdProvider>
      <ReactFlowProvider>
        <CanvasHydrator
          canvas={canvas}
          tablesApp={tablesApp}
          interfacesApp={interfacesApp}
          chatbotsApp={chatbotsApp}
          agentsApp={agentsApp}
          isPublic={true}
          session={session}
          account={account}
          toggles={toggles}
        >
          <CanvasCopilotHydrator>
            <Notifications />
            <BuilderNav>
              <Page />
            </BuilderNav>
          </CanvasCopilotHydrator>
        </CanvasHydrator>
      </ReactFlowProvider>
    </CurrentAccountIdProvider>
  );
}

function Page() {
  const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] =
    useAtom(isKeyboardShortcutsModalOpenAtom);
  return (
    <>
      {isKeyboardShortcutsModalOpen && (
        <KeyboardShortcutsModal
          onClosed={() => setIsKeyboardShortcutsModalOpen(false)}
        />
      )}
      <div className={styles.wrapper}>
        <Canvas>
          <CanvasBackground />
        </Canvas>
      </div>
    </>
  );
}

function useEmitPageViewEvent() {
  const currentAccountId = useCurrentAccountId();
  useEffect(() => {
    setTimeout(() => {
      window.EventToolkit.emit(
        {
          system_name: "canvas",
          account_id: currentAccountId,
        },
        PAGE_VIEW_EVENT,
      );
      // For some reason the cdn JS that we load with the event toolkit that populates
      // window.EventToolkit doesn't seem to be available immediately. If we wait, it is more likely to be available.
      // This is a temporary fix until we can figure out why the JS isn't available immediately.
    }, 100);
  }, [currentAccountId]);
}
